import { useState } from 'react';
import { FaTimes, FaLanguage, FaArrowRight, FaCopy, FaCheck } from 'react-icons/fa';
import { getFirestore } from 'firebase/firestore';
import { translateToText } from '../utilities/extensions/translateToText';
import Constants from '../utilities/constants/Constants.jsx';

const HelpModal = ({ isOpen, onClose }) => {
  const [kannadaInput, setKannadaInput] = useState('');
  const [kannadaOutput, setKannadaOutput] = useState('');
  const [englishInput, setEnglishInput] = useState('');
  const [englishOutput, setEnglishOutput] = useState('');
  const [isTranslatingKannada, setIsTranslatingKannada] = useState(false);
  const [isTranslatingEnglish, setIsTranslatingEnglish] = useState(false);
  const [kannadaError, setKannadaError] = useState('');
  const [englishError, setEnglishError] = useState('');
  const [kannadaCopied, setKannadaCopied] = useState(false);
  const [englishCopied, setEnglishCopied] = useState(false);

  if (!isOpen) return null;

  const handleTranslateKannadaToEnglish = async () => {
    if (!kannadaInput.trim()) return;
    
    setIsTranslatingKannada(true);
    setKannadaError('');
    setKannadaOutput('');
    
    try {
      const db = getFirestore();
      const translatedText = await translateToText(
        kannadaInput, 
        db, 
        5, 
        2000, 
        Constants.LANGUAGES.KANNADA, 
        Constants.LANGUAGES.ENGLISH
      );
      setKannadaOutput(translatedText);
    } catch (error) {

      
      // Check if it's a permissions error
      if (error.message.includes('permissions') || error.message.includes('Missing or insufficient')) {
        setKannadaError('Translation service is not configured yet. Please contact administrator to set up Firestore permissions for the "translations" collection.');
      } else {
        setKannadaError('Translation failed. Please try again.');
      }
    } finally {
      setIsTranslatingKannada(false);
    }
  };

  const handleTranslateEnglishToKannada = async () => {
    if (!englishInput.trim()) return;
    
    setIsTranslatingEnglish(true);
    setEnglishError('');
    setEnglishOutput('');
    
    try {
      const db = getFirestore();
      // Translate English to Kannada using the new language parameters
      const translatedText = await translateToText(
        englishInput, 
        db, 
        5, 
        2000, 
        Constants.LANGUAGES.ENGLISH, 
        Constants.LANGUAGES.KANNADA
      );
      setEnglishOutput(translatedText);
    } catch (error) {

      
      // Check if it's a permissions error
      if (error.message.includes('permissions') || error.message.includes('Missing or insufficient')) {
        setEnglishError('Translation service is not configured yet. Please contact administrator to set up Firestore permissions for the "translations" collection.');
      } else {
        setEnglishError('Translation failed. Please try again.');
      }
    } finally {
      setIsTranslatingEnglish(false);
    }
  };

  const handleCopyKannada = async () => {
    if (kannadaOutput) {
      try {
        await navigator.clipboard.writeText(kannadaOutput);
        setKannadaCopied(true);
        setTimeout(() => setKannadaCopied(false), 2000);
      } catch (error) {

        // Ignore errors
      }
    }
  };

  const handleCopyEnglish = async () => {
    if (englishOutput) {
      try {
        await navigator.clipboard.writeText(englishOutput);
        setEnglishCopied(true);
        setTimeout(() => setEnglishCopied(false), 2000);
      } catch (error) {

        // Ignore errors
      }
    }
  };

  const handleClose = () => {
    setKannadaInput('');
    setKannadaOutput('');
    setEnglishInput('');
    setEnglishOutput('');
    setKannadaCopied(false);
    setEnglishCopied(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-gradient-to-r from-indigo-600 to-purple-600 text-white p-6 rounded-t-2xl flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <FaLanguage className="text-3xl" />
            <h2 className="text-2xl font-bold">Translation Help</h2>
          </div>
          <button
            onClick={handleClose}
            className="p-2 hover:bg-white/20 rounded-full transition-colors"
            aria-label="Close help"
          >
            <FaTimes className="text-2xl" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-8">
          {/* Row 1: Kannada to English */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-gray-900">Kannada to English</h3>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={kannadaInput}
                onChange={(e) => setKannadaInput(e.target.value)}
                placeholder="ಇಲ್ಲಿ ಕನ್ನಡ ಪಠ್ಯವನ್ನು ನಮೂದಿಸಿ..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleTranslateKannadaToEnglish();
                  }
                }}
              />
              <button
                onClick={handleTranslateKannadaToEnglish}
                disabled={!kannadaInput.trim() || isTranslatingKannada}
                className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                title="Translate"
              >
                {isTranslatingKannada ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <FaArrowRight className="text-xl" />
                )}
              </button>
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={kannadaOutput}
                  readOnly
                  placeholder="English translation..."
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl bg-gray-50 text-lg"
                />
                {kannadaOutput && (
                  <button
                    onClick={handleCopyKannada}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-600 hover:text-indigo-600 transition-colors rounded-lg hover:bg-gray-200"
                    title="Copy to clipboard"
                  >
                    {kannadaCopied ? (
                      <FaCheck className="text-green-600" />
                    ) : (
                      <FaCopy />
                    )}
                  </button>
                )}
              </div>
            </div>
            {kannadaError && (
              <p className="text-red-600 text-sm">{kannadaError}</p>
            )}
          </div>

          {/* Divider */}
          <div className="border-t border-gray-200"></div>

          {/* Row 2: English to Kannada */}
          <div className="space-y-3">
            <h3 className="text-lg font-bold text-gray-900">English to Kannada</h3>
            <div className="flex items-center gap-3">
              <input
                type="text"
                value={englishInput}
                onChange={(e) => setEnglishInput(e.target.value)}
                placeholder="Enter English text here..."
                className="flex-1 px-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:border-transparent text-lg"
                onKeyPress={(e) => {
                  if (e.key === 'Enter') {
                    handleTranslateEnglishToKannada();
                  }
                }}
              />
              <button
                onClick={handleTranslateEnglishToKannada}
                disabled={!englishInput.trim() || isTranslatingEnglish}
                className="p-3 bg-indigo-600 text-white rounded-xl hover:bg-indigo-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed shadow-lg"
                title="Translate"
              >
                {isTranslatingEnglish ? (
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                ) : (
                  <FaArrowRight className="text-xl" />
                )}
              </button>
              <div className="flex-1 relative">
                <input
                  type="text"
                  value={englishOutput}
                  readOnly
                  placeholder="ಕನ್ನಡ ಅನುವಾದ..."
                  className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-xl bg-gray-50 text-lg"
                />
                {englishOutput && (
                  <button
                    onClick={handleCopyEnglish}
                    className="absolute right-2 top-1/2 -translate-y-1/2 p-2 text-gray-600 hover:text-indigo-600 transition-colors rounded-lg hover:bg-gray-200"
                    title="Copy to clipboard"
                  >
                    {englishCopied ? (
                      <FaCheck className="text-green-600" />
                    ) : (
                      <FaCopy />
                    )}
                  </button>
                )}
              </div>
            </div>
            {englishError && (
              <p className="text-red-600 text-sm">{englishError}</p>
            )}
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-gray-50 p-4 rounded-b-2xl border-t border-gray-200">
          <button
            onClick={handleClose}
            className="w-full px-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-xl font-semibold hover:from-indigo-700 hover:to-purple-700 transition-all duration-300 shadow-lg"
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default HelpModal;
